import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNotification } from "../../../hooks/useNotification";
import {
  useBulkUpdatePurchaseOrderStatusMutation,
  useCancelPurchaseOrdersMutation,
} from "../../../store/apiSlice/purchaseOrderApiSlice";
import { FormLabel } from "../../../components/atoms/label";
import { FormOption, FormSelect } from "../../../components/atoms/select/select";
import { ModalBodyT1 } from "../../../components/organisms/Modal/Body";
import { ModalOverlay, ModalT1 } from "../../../components/organisms/Modal";
import { PortalFooter } from "../../../components/molecules/Footer";
import { PortalHeader } from "../../../components/molecules/Header";
import {
  CloseModalBody,
  FieldBlock,
  HintText,
  IntroText,
  NameChip,
  NameChipList,
} from "../../shared/closeDocument/closeDocument.styles";
import {
  defaultManualPoStatusTarget,
  PO_CANCEL_TARGET_STATUS,
  PO_CLOSE_TARGET_STATUS,
} from "../constants/manualStatusWorkflow";

/**
 * Confirm bulk advance of purchase orders through the manual status workflow.
 * Choosing Closed hands off to the close review modal via `onChooseClose`.
 */
export const UpdatePurchaseOrderStatusModal = ({
  closeModal,
  orders = [],
  currentStatus,
  allowedTargets = [],
  cancelOnly = false,
  setSelectedOrder,
  onChooseClose,
}) => {
  const notify = useNotification();
  const [bulkUpdateStatus, { isLoading: isUpdatingStatus }] =
    useBulkUpdatePurchaseOrderStatusMutation();
  const [cancelPurchaseOrders, { isLoading: isCancelling }] = useCancelPurchaseOrdersMutation();
  const isLoading = isUpdatingStatus || isCancelling;

  const [targetStatus, setTargetStatus] = useState("");

  useEffect(() => {
    setTargetStatus(defaultManualPoStatusTarget(currentStatus, allowedTargets));
  }, [allowedTargets, currentStatus]);

  const isCancelTarget = targetStatus === PO_CANCEL_TARGET_STATUS;
  const isCloseTarget = targetStatus === PO_CLOSE_TARGET_STATUS;
  const orderNoun = orders.length === 1 ? "this order" : `these ${orders.length} orders`;

  const handleOverlayClick = event => {
    if (event.target === event.currentTarget) {
      closeModal();
    }
  };

  const stopPropagation = event => {
    event.stopPropagation();
  };

  const handleSubmit = async () => {
    if (!targetStatus) {
      notify({ type: "error", message: "Select a status." });
      return;
    }
    if (isCloseTarget) {
      onChooseClose?.();
      return;
    }

    const orderIds = orders.map(item => item.id);

    try {
      if (isCancelTarget) {
        const response = await cancelPurchaseOrders({ ids: orderIds }).unwrap();
        notify({ type: "success", message: response.detail });
      } else {
        const response = await bulkUpdateStatus({
          ids: orderIds,
          status: targetStatus,
        }).unwrap();
        notify({ type: "success", message: response.detail });
      }
      setSelectedOrder?.([]);
      closeModal();
    } catch (error) {
      const data = error?.data || {};
      const fieldMessage = [data.ids, data.status, data.non_field_errors]
        .flat()
        .filter(Boolean)
        .join(" ");
      notify({
        type: "error",
        message:
          data.detail ||
          fieldMessage ||
          (isCancelTarget
            ? "Failed to cancel purchase orders."
            : "Failed to update purchase order status."),
      });
    }
  };

  const submitLabel = isCancelTarget
    ? "Cancel orders"
    : isCloseTarget
      ? "Continue"
      : "Update status";

  const modalContent = (
    <ModalOverlay onClickCallback={handleOverlayClick}>
      <ModalT1 $minWidth={520} $maxWidth={640} onClick={stopPropagation}>
        <PortalHeader
          title={cancelOnly ? "Cancel purchase orders" : "Update purchase order status"}
          onClose={closeModal}
        />
        <ModalBodyT1>
          <CloseModalBody>
            <IntroText>
              {cancelOnly ? (
                <>You&apos;re about to cancel {orderNoun}. Are you sure?</>
              ) : currentStatus ? (
                <>
                  Change {orderNoun} from <strong>{currentStatus}</strong> to a new status.
                </>
              ) : (
                <>Choose a new status for {orderNoun}.</>
              )}
            </IntroText>

            <NameChipList>
              {orders.map(item => (
                <NameChip key={item.id}>{item.name || item.order_no || `#${item.id}`}</NameChip>
              ))}
            </NameChipList>

            <FieldBlock>
              <FormLabel htmlFor="update-po-status-target">New status</FormLabel>
              <FormSelect
                id="update-po-status-target"
                value={targetStatus}
                onChange={event => setTargetStatus(event.target.value)}
                disabled={isLoading}
              >
                {allowedTargets.map(status => (
                  <FormOption key={status} value={status}>
                    {status}
                  </FormOption>
                ))}
              </FormSelect>
            </FieldBlock>

            {isCloseTarget && (
              <HintText>Next you&apos;ll review any warnings and choose a close reason.</HintText>
            )}
          </CloseModalBody>
        </ModalBodyT1>
        <PortalFooter
          className="for-confirm-modal"
          applyTitle={submitLabel}
          cancelTitle="Back"
          onApply={handleSubmit}
          onCancel={closeModal}
          isDisabled={!targetStatus || isLoading}
          isLoading={isLoading}
          applyVariant={isCancelTarget ? "danger" : "primary"}
        />
      </ModalT1>
    </ModalOverlay>
  );

  const portalRoot = document.getElementById("root-modals") || document.body;
  return createPortal(modalContent, portalRoot);
};
